import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  BarChart3, 
  TrendingUp, 
  Clock
} from 'lucide-react';
import { checklistKpiApi } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';

export function ChecklistKpiPage() {
  const { hapticFeedback } = useTelegram();
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  });

  const { data: kpi, isLoading } = useQuery({
    queryKey: ['checklist-kpi', dateRange],
    queryFn: () =>
      checklistKpiApi.get({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate).toISOString(),
      }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          KPI по чек-листам
        </h1>
      </div>

      {/* Фильтр по периоду */}
      <Card className="p-4">
        <p className="text-sm font-medium text-tg-text mb-3">Период</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="date"
            label="С"
            value={dateRange.startDate}
            onChange={(e) => {
              setDateRange({ ...dateRange, startDate: e.target.value });
              hapticFeedback('light');
            }}
          />
          <Input
            type="date"
            label="По"
            value={dateRange.endDate}
            onChange={(e) => {
              setDateRange({ ...dateRange, endDate: e.target.value });
              hapticFeedback('light');
            }}
          />
        </div>
      </Card>

      {/* Метрики */}
      {kpi && (
        <div className="space-y-3">
          {/* Основные показатели */}
          <Card className="p-4">
            <h2 className="text-lg font-semibold text-tg-text mb-4">Основные показатели</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-tg-hint">Назначено</p>
                <p className="text-2xl font-bold text-tg-text">{kpi.assignedCount}</p>
              </div>
              <div>
                <p className="text-sm text-tg-hint">Выполнено</p>
                <p className="text-2xl font-bold text-tg-text">{kpi.completedCount}</p>
              </div>
              <div>
                <p className="text-sm text-tg-hint">Принято</p>
                <p className="text-2xl font-bold text-green-600">{kpi.acceptedCount}</p>
              </div>
              <div>
                <p className="text-sm text-tg-hint">Просрочено</p>
                <p className="text-2xl font-bold text-red-600">{kpi.overdueCount}</p>
              </div>
              <div>
                <p className="text-sm text-tg-hint">Отклонено</p>
                <p className="text-2xl font-bold text-orange-600">{kpi.rejectedCount}</p>
              </div>
              <div>
                <p className="text-sm text-tg-hint">Принято в срок</p>
                <p className="text-2xl font-bold text-green-600">{kpi.ontimeAcceptedCount}</p>
              </div>
            </div>
          </Card>

          {/* Проценты */}
          <Card className="p-4">
            <h2 className="text-lg font-semibold text-tg-text mb-4">Проценты</h2>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-tg-text">Процент выполнения</span>
                  <span className="text-sm font-semibold text-tg-text">
                    {kpi.completionRate?.toFixed(1) || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${kpi.completionRate || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-tg-text">Процент принятия</span>
                  <span className="text-sm font-semibold text-tg-text">
                    {kpi.acceptanceRate?.toFixed(1) || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${kpi.acceptanceRate || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-tg-text">Процент в срок</span>
                  <span className="text-sm font-semibold text-tg-text">
                    {kpi.ontimeRate?.toFixed(1) || 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${kpi.ontimeRate || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Дополнительная информация */}
          {kpi.avgApprovalTime && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-tg-hint" />
                <span className="text-sm font-medium text-tg-text">Среднее время до приёмки</span>
              </div>
              <p className="text-2xl font-bold text-tg-text">
                {Math.floor(kpi.avgApprovalTime / 3600)}ч {Math.floor((kpi.avgApprovalTime % 3600) / 60)}м
              </p>
            </Card>
          )}

          {kpi.dailyStreak !== null && kpi.dailyStreak !== undefined && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-tg-hint" />
                <span className="text-sm font-medium text-tg-text">Серия (streak)</span>
              </div>
              <p className="text-2xl font-bold text-tg-text">{kpi.dailyStreak} дней</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

